import Vue from 'vue';
import Vuex from 'vuex';
import i18n from '@/i18n';
import {
    ICo2ReportResponseEntity,
    IInvoiceByDateRequest,
    IReportInvoiceByDateResponce,
    IReportServiceByDateResponce,
    IServiceByDateRequest
} from '@/domain/IReport';
import { ReportApi } from '@/services/ReportApi';
import { BaseApi } from '@/services/BaseApi';
import { IToplevel } from '@/domain/IToplevel';
import { ICountry } from '@/domain/ICountry';
import { AccountApi } from '@/services/AccountApi';
import { ICustomer } from '@/domain/ICustomer';
import { IAuthorizers } from '@/domain/IAuthorizers';
import { IWebContentsRequest, IWebContentsResponce } from '@/domain/IWebContents';
import { IBpTransactionsRequest, IBpTransactionsResponce } from '@/domain/IBpTransactions';
import { ISubscriptionsRequest } from '@/domain/ISubscriptions';
import { InfoApi } from '@/services/InfoApi';
import {
    IBirthdateRequest,
    IDeleteBonuscardRequest,
    IDeleteCommunicationDeviceRequest,
    ISaveBonuscardRequest,
    ISaveCommunicationDeviceRequest
} from '@/domain/ISaveInfo';
import router from '@/router';
import { IAuthorizedPersonRequest } from '@/domain/ITravelManagerOrAuthorizedPerson';
import createPersistedState from 'vuex-persistedstate';

Vue.use(Vuex);

export default new Vuex.Store({
    state: {
        token: null as string,
        expiresAt: null,
        customerName: null as string,
        invoiceExtraFields: false as boolean,
        customerId: null as number,
        customerType: null as string,
        authorizers: [] as IAuthorizers[],
        customerInfo: null as ICustomer,
        bpTransactions: null as IBpTransactionsResponce,
        toplevel: [] as IToplevel[],
        country: [] as ICountry[],
        webContents: null as IWebContentsResponce,
        logOut: false as boolean,
        isFetchingCustomerInfo: false as boolean,
        isUpdatingCustomerInfo: false as boolean,
        customerInfoLoadingError: false as boolean,
        eIdLogin: false as boolean
    },

    mutations: {
        setLoginInfo(
            state,
            user: {
                access_token: string;
                expires_at: number;
                profile: {
                    acr: string;
                    customer_id: string;
                    family_name: string;
                    given_name: string;
                    groupIds: string[];
                    iss: string;
                    realmName: string;
                    sid: string;
                    sub: string;
                    uniqueSecurityName: string;
                };
            } | null
        ) {
            state.token = user ? user.access_token : null;
            state.expiresAt = user ? user.expires_at : null;
            state.eIdLogin = user?.profile?.acr === 'strong';
        },
        setCustomerInfo(state, customer: ICustomer | null) {
            state.customerInfo = customer;
        },
        setBpTransactions(state, transactions: IBpTransactionsResponce | null) {
            state.bpTransactions = transactions;
        },
        setCustomerId(state, id: number | null) {
            state.customerId = id;
        },
        setCustomerType(state, type: string | null) {
            state.customerType = type;
        },
        setCustomerName(state, name: string | null) {
            state.customerName = name;
        },
        setInvoiceExtraFields(state, invoiceExtraFields: boolean | null) {
            state.invoiceExtraFields = invoiceExtraFields;
        },
        setLogOut(state, logout: boolean | null) {
            state.logOut = logout;
        },
        setAuthorizers(state, authorizers: IAuthorizers[]) {
            state.authorizers = authorizers;
        },
        setToplevel(state, toplevel: IToplevel[]) {
            state.toplevel = toplevel;
        },
        setCountry(state, country: ICountry[]) {
            state.country = country;
        },
        setWebContents(state, webContentsResponce: IWebContentsResponce) {
            state.webContents = webContentsResponce;
        },
        updateAuthorizers(state, updatedAuthorizers) {
            state.authorizers = updatedAuthorizers;
        },
        setFetchingCustomerInfo(state, status: boolean | null) {
            state.isFetchingCustomerInfo = status;
        },
        setUpdatingCustomerInfo(state, status: boolean) {
            state.isUpdatingCustomerInfo = status;
        },
        setCustomerInfoLoadingError(state, error: boolean) {
            state.customerInfoLoadingError = error;
        }
    },
    getters: {
        isAuthenticated: (state) => {
            const expiresAt = state.expiresAt;
            return expiresAt && expiresAt * 1000 > Date.now();
        },
        getCustomerId(context): number {
            return context.customerId;
        },
        getCustomerType(context): string {
            return context.customerType;
        },
        getWebContents(context): IWebContentsResponce | null {
            return context.webContents;
        },
        getCustomerInfo(context): ICustomer | null {
            return context.customerInfo;
        },
        getBpTransactions(context): IBpTransactionsResponce | null {
            return context.bpTransactions;
        },
        getAuthorizers(context): IAuthorizers[] | [] {
            return context.authorizers;
        },
        isFetchingCustomerInfo(context) {
            return context.isFetchingCustomerInfo;
        },
        isUpdatingCustomerInfo(context) {
            return context.isUpdatingCustomerInfo;
        },
        getCustomerInfoLoadingError(context) {
            return context.customerInfoLoadingError;
        },
        shouldAskForPersonalDetails(context) {
            const customerInfo: ICustomer = context.customerInfo;
            if (customerInfo?.customertype === 'P' && !context.isFetchingCustomerInfo) {
                const email = customerInfo.contacts?.find((contact) => contact.name === 'E-mail'); // Only possible to edit if eId login or through Estravel employee
                const mobile = customerInfo.contacts?.find((contact) => contact.name === 'Mobile Phone');
                const personalCode = customerInfo?.personal_code;
                const birthDate = customerInfo?.birth_date;
                if (!email?.value?.length || !mobile?.value?.length || (!personalCode?.length && !birthDate?.length)) {
                    return true;
                }
            }
            return false;
        }
    },
    actions: {
        clear(context): void {
            if (context.getters.isAuthenticated) {
                context.commit('setLogOut', true);
            }
            context.commit('setLoginInfo', null);
            context.commit('setAuthorizers', []);
            context.commit('setCustomerId', null as string);
            context.commit('setCustomerName', null as string);
            context.commit('setCustomerInfo', null as ICustomer);
            context.commit('setBpTransactions', null);
            context.commit('setCustomerType', null);
        },
        async notifyAndLogout(context) {
            await context.dispatch('clear');
            if (router.currentRoute.name !== 'AccountLogin') {
                Vue.notify({
                    group: 'auth',
                    title: i18n.t('notifications.session_expired.title') as string,
                    text: i18n.t('notifications.session_expired.text') as string,
                    type: 'warn'
                });
                await router.push({ name: 'AccountLogin' });
            }
        },
        async authenticateUser(context) {
            context.commit('setFetchingCustomerInfo', true);
            if (!context.getters.isAuthenticated) {
                context.commit('setFetchingCustomerInfo', false);
                return false;
            }
            let customerInfo: ICustomer | null = null;
            try {
                customerInfo = await AccountApi.getCustomerInfo(context.state.customerId);
            } catch (error) {
                context.commit('setFetchingCustomerInfo', false);
                context.commit('setCustomerInfoLoadingError', true);
                return false;
            }
            if (!customerInfo) {
                context.commit('setFetchingCustomerInfo', false);
                context.commit('setCustomerInfoLoadingError', true);
                return false;
            }
            context.commit('setCustomerInfoLoadingError', false);
            context.commit('setCustomerInfo', customerInfo);

            const dayTo = new Date();
            const dayFrom = new Date(dayTo.getFullYear() - 1, 0, 1);
            const bpTransactionsRequest: IBpTransactionsRequest = {
                customerid: customerInfo.customerid,
                date_from:
                    dayFrom.getFullYear() +
                    '-' +
                    String(dayFrom.getMonth() + 1).padStart(2, '0') +
                    '-' +
                    String(dayFrom.getDate()).padStart(2, '0'),
                date_to:
                    dayTo.getFullYear() +
                    '-' +
                    String(dayTo.getMonth() + 1).padStart(2, '0') +
                    '-' +
                    String(dayTo.getDate()).padStart(2, '0')
            };
            const bpTransactions = await BaseApi.getBpTransactions(bpTransactionsRequest);

            context.commit('setBpTransactions', bpTransactions);
            context.commit('setInvoiceExtraFields', customerInfo.invoiceextrafields);
            context.commit('setCustomerName', customerInfo.firstname + ' ' + customerInfo.lastname);
            context.commit('setCustomerId', customerInfo.customerid);
            context.commit('setCustomerType', customerInfo.customertype);
            if (customerInfo.authorizers === null) {
                console.log('customerInfo.authorizers === null');
            } else if (customerInfo.authorizers.length > 0) {
                const authorizers = [];
                authorizers.push({
                    customerId: customerInfo.customerid,
                    name: 'nav.top.personal_acc',
                    endDate: '',
                    startDate: ''
                });
                customerInfo.authorizers.forEach((authorizer) => {
                    authorizers.push(authorizer);
                });
                context.commit('setAuthorizers', authorizers);
            }
            await Promise.all([context.dispatch('getCountry'), context.dispatch('getToplevel'), context.dispatch('getWebContents')]);
            context.commit('setFetchingCustomerInfo', false);
            return context.state.customerInfo !== null;
        },
        async getToplevel(context): Promise<void> {
            if (context.getters.isAuthenticated) {
                const toplevel = await BaseApi.getToplevel();
                context.commit('setToplevel', toplevel);
            } else await context.dispatch('notifyAndLogout');
        },
        async getCountry(context): Promise<void> {
            if (context.getters.isAuthenticated) {
                const country = await BaseApi.getCountry();
                context.commit('setCountry', country);
            } else await context.dispatch('notifyAndLogout');
        },
        async getServicesByDate(context, ServiceByDateRequest: IServiceByDateRequest): Promise<IReportServiceByDateResponce> {
            if (context.getters.isAuthenticated) {
                const ReportServiceByDateResponce = await ReportApi.getServicesByDate(ServiceByDateRequest);
                return ReportServiceByDateResponce;
            } else await context.dispatch('notifyAndLogout');
        },
        async getInvoicesByDate(context, InvoiceByDateRequest: IInvoiceByDateRequest): Promise<IReportInvoiceByDateResponce> {
            if (context.getters.isAuthenticated) {
                const ReportInvoiceByDateResponce = await ReportApi.getInvoicesByDate(InvoiceByDateRequest);
                return ReportInvoiceByDateResponce;
            } else await context.dispatch('notifyAndLogout');
        },
        async subscribe(context, SubscribeRequest: ISubscriptionsRequest): Promise<boolean> {
            if (context.getters.isAuthenticated) {
                const subscribeResponce = await InfoApi.subscribe(SubscribeRequest);
                return subscribeResponce?.status === 200;
            } else await context.dispatch('notifyAndLogout');
        },
        async unsubscribe(context, UnSubscribeRequest: ISubscriptionsRequest): Promise<boolean> {
            if (context.getters.isAuthenticated) {
                const unSubscribeResponce = await InfoApi.unsubscribe(UnSubscribeRequest);
                return unSubscribeResponce?.status === 200;
            } else await context.dispatch('notifyAndLogout');
        },

        async saveBirthdate(context, BirthdateRequest: IBirthdateRequest): Promise<boolean> {
            if (context.getters.isAuthenticated) {
                const res = await InfoApi.saveBirthdate(BirthdateRequest);
                return res?.status === 200;
            } else await context.dispatch('notifyAndLogout');
        },

        async saveCommunicationDevice(context, SaveCommunicationDeviceRequest: ISaveCommunicationDeviceRequest): Promise<boolean> {
            if (context.getters.isAuthenticated) {
                const saveCommunicationDeviceResponce = await InfoApi.saveCommDevice(SaveCommunicationDeviceRequest);
                return [200, 201].includes(saveCommunicationDeviceResponce?.status);
            } else await context.dispatch('notifyAndLogout');
        },

        async deleteCommunicationDevice(context, DeleteCommunicationDeviceRequest: IDeleteCommunicationDeviceRequest): Promise<boolean> {
            if (context.getters.isAuthenticated) {
                const deleteCommunicationDeviceResponce = await InfoApi.deleteCommDevice(DeleteCommunicationDeviceRequest);
                return deleteCommunicationDeviceResponce?.status === 200;
            } else await context.dispatch('notifyAndLogout');
        },

        async saveBonuscard(context, SaveBonuscardRequest: ISaveBonuscardRequest): Promise<boolean> {
            if (context.getters.isAuthenticated) {
                const saveBonuscardResponce = await InfoApi.saveBonuscard(SaveBonuscardRequest);
                return [200, 201].includes(saveBonuscardResponce?.status);
            } else await context.dispatch('notifyAndLogout');
        },

        async deleteBonuscard(context, DeleteBonuscardRequest: IDeleteBonuscardRequest): Promise<boolean> {
            if (context.getters.isAuthenticated) {
                const deleteBonuscardResponce = await InfoApi.deleteBonuscard(DeleteBonuscardRequest);
                return deleteBonuscardResponce?.status === 200;
            } else await context.dispatch('notifyAndLogout');
        },

        async addAuthorizedPerson(context, AuthorizedPersonRequest: IAuthorizedPersonRequest): Promise<any> {
            if (context.getters.isAuthenticated) {
                return await InfoApi.addAuthorizedPerson(AuthorizedPersonRequest);
            } else await context.dispatch('notifyAndLogout');
        },

        async updateAuthorizedPerson(context, AuthorizedPersonRequest: IAuthorizedPersonRequest): Promise<any> {
            if (context.getters.isAuthenticated) {
                const res = await InfoApi.updateAuthorizedPerson(AuthorizedPersonRequest);
                return res?.status === 200;
            } else await context.dispatch('notifyAndLogout');
        },

        async updateCustomerInfo(context): Promise<void> {
            if (!context.getters.isAuthenticated) {
                await context.dispatch('notifyAndLogout');
                return;
            }
            context.commit('setUpdatingCustomerInfo', true);
            try {
                const customerInfo = await AccountApi.getCustomerInfo(context.state.customerId);
                if (customerInfo === null) {
                    return;
                }
                context.commit('setCustomerInfo', customerInfo);
                context.commit('setInvoiceExtraFields', customerInfo.invoiceextrafields);
                context.commit('setCustomerName', customerInfo.firstname + ' ' + customerInfo.lastname);
                context.commit('setCustomerId', customerInfo.customerid);
                context.commit('setCustomerType', customerInfo.customertype);
                if (customerInfo.authorizers === null) {
                    console.info('customerInfo.authorizers === null');
                } else if (customerInfo.authorizers.length > 0) {
                    const authorizers = [];
                    authorizers.push({
                        customerId: customerInfo.customerid,
                        name: 'nav.top.personal_acc',
                        endDate: '',
                        startDate: ''
                    });
                    customerInfo.authorizers.forEach((authorizer) => {
                        authorizers.push(authorizer);
                    });
                    context.commit('setAuthorizers', authorizers);
                }
            } finally {
                context.commit('setUpdatingCustomerInfo', false);
            }
        },

        async getWebContents(context): Promise<void> {
            if (context.getters.isAuthenticated) {
                const customerInfo: ICustomer = context.state.customerInfo;
                const webContentsRequest: IWebContentsRequest = {
                    customerid: customerInfo.customerid,
                    customer_type: customerInfo.customertype,
                    categories: customerInfo.customer_categories == null ? [] : customerInfo.customer_categories
                };
                const webContents: IWebContentsResponce = await BaseApi.getWebContents(webContentsRequest);
                context.commit('setWebContents', webContents);
            } else await context.dispatch('notifyAndLogout');
        },

        async addCardToWallet(context, payload: { cardNumber: string; walletType: 'APPLE' | 'GOOGLE' }): Promise<void> {
            if (context.getters.isAuthenticated) {
                const response = await InfoApi.addCardToWallet(payload);
                if (response?.status === 200) {
                    if (payload.walletType === 'APPLE') {
                        const blob = new Blob([response.data], { type: 'application/vnd.apple.pkpass' });
                        const url = window.URL.createObjectURL(blob);
                        window.location.href = url;
                    } else if (payload.walletType === 'GOOGLE') {
                        window.location.href = response.data;
                    }
                } else {
                    Vue.notify({
                        group: 'api',
                        title: i18n.t(`notifications.add_to_wallet_failed.${payload.walletType}`) as string,
                        type: 'warn'
                    });
                }
            } else await context.dispatch('notifyAndLogout');
        },

        async requestNewCard(context): Promise<void> {
            if (context.getters.isAuthenticated) {
                const response = await InfoApi.requestNewCard();
                if (response?.status === 200) {
                    await context.dispatch('updateCustomerInfo');
                } else {
                    Vue.notify({
                        group: 'api',
                        title: i18n.t(`notifications.create_new_customercard_failed`) as string,
                        text: response?.data,
                        type: 'warn'
                    });
                }
            } else await context.dispatch('notifyAndLogout');
        },

        async getCo2ReportByDate(context, ServiceByDateRequest: Partial<IServiceByDateRequest>): Promise<ICo2ReportResponseEntity[] | []> {
            if (context.getters.isAuthenticated) {
                const co2ReportResponse = await ReportApi.getCo2ReportByDate(ServiceByDateRequest);
                return co2ReportResponse ?? [];
            } else await context.dispatch('notifyAndLogout');
            return [];
        },

        async addPersonalCode(context, request: { personalCode: string; customerId: string }): Promise<boolean> {
            if (context.getters.isAuthenticated) {
                const response = await InfoApi.addPersonalCode(request);
                return [200, 201].includes(response?.status);
            } else await context.dispatch('notifyAndLogout');
        },

        async saveGender(context, request: { gender: 'F' | 'M'; customerId: string }): Promise<boolean> {
            if (context.getters.isAuthenticated) {
                const response = await InfoApi.saveGender(request);
                return [200, 201].includes(response?.status);
            } else await context.dispatch('notifyAndLogout');
        },

        async saveLanguage(context, request: { language: string; customerId: string }): Promise<boolean> {
            if (context.getters.isAuthenticated) {
                const response = await InfoApi.saveLanguage(request);
                return [200, 201].includes(response?.status);
            } else await context.dispatch('notifyAndLogout');
        }
    },
    modules: {},
    plugins: [
        createPersistedState({
            storage: window.localStorage,
            key: 'estravel-self-service-store'
        })
    ]
});
